import type { ReactNode } from 'react'
import { Link } from 'react-router'
import upperFirst from 'lodash/upperFirst'
import type { ColumnDef } from '@tanstack/react-table'
import { Info } from 'lucide-react'
import {
  PERMISSION_ENTITY_INVOICE,
  PERMISSION_ENTITY_PROOF_OF_ACCEPTANCE,
  PERMISSION_ENTITY_PURCHASE_ORDER
} from '@klear/constants'
import { KlearDate } from '@klear/date'
import type { Money } from '@klear/money'
import type { InvoiceOffer } from '@klear/invoice-offer/invoice-offer'
import { invoiceOfferAdvanceRateFormatted } from '@klear/invoice-offer/list-display'
import type { ReceivableDocuments, ReceivableRow } from '@klear/order-event/receivable'
import type { OrderEventFilePreviewContext } from '@klear/order-event/file-preview-display'
import type { UploadHints } from '@klear/schema/document-processing'
import type { OrderEventDocumentRef } from '@klear/upload/order-event-documents'
import { Badge } from '@klear/ui/components/badge'
import { Button } from '@klear/ui/components/button'
import { DataTable } from '@klear/ui/components/data-table'
import { Tooltip, TooltipContent, TooltipTrigger } from '@klear/ui/components/tooltip'
import { cn } from '@klear/ui/lib/utils'
import {
  AttachFilesButton,
  ReceivableFiles,
  SendFilesButton,
  type ReceivableFileGroup
} from '@/app/receivables/receivable-files'

export type ReceivableTableBaseContext = {
  traderId: number
  isLoading: boolean
  documentsFor: (orderEventId: number | undefined) => readonly OrderEventDocumentRef[]
  orderHref: (orderId: number, orderEventId: number) => string
}

export type ReceivableTableContext = ReceivableTableBaseContext & {
  canAccept: boolean
  canCancel: boolean
  onAccept: (offer: InvoiceOffer) => void
  onCancel: (offer: InvoiceOffer) => void
  onPayNow: (offer: InvoiceOffer) => void
}

type DocumentKey = keyof ReceivableDocuments

const ESTIMATE_TIP = 'Pricing is estimated and subject to change until the offer is funded.'

const DOCUMENTS = [
  { key: 'purchaseOrder', title: 'Purchase order', eventType: PERMISSION_ENTITY_PURCHASE_ORDER },
  { key: 'invoice', title: 'Invoice', eventType: PERMISSION_ENTITY_INVOICE },
  { key: 'proofOfAcceptance', title: 'Proof of acceptance', eventType: PERMISSION_ENTITY_PROOF_OF_ACCEPTANCE }
] as const satisfies readonly { key: DocumentKey; title: string; eventType: string }[]

type DocumentStatus = ReceivableDocuments[DocumentKey]['status']

function missingDocumentHints(key: DocumentKey, row: ReceivableRow): UploadHints | null {
  switch (key) {
    case 'purchaseOrder':
      return {
        traderSide: 'sells',
        documentKind: 'purchase_order',
        orderId: row.invoice.orderId,
        childOrderEventId: row.invoice.id
      }
    case 'proofOfAcceptance':
      return {
        traderSide: 'sells',
        documentKind: 'delivery_or_acceptance',
        orderId: row.invoice.orderId,
        parentOrderEventId: row.invoice.id
      }
    default:
      return null
  }
}

const DOCUMENT_STATUS_BADGE: Record<DocumentStatus, { label: string; className: string }> = {
  missing: {
    label: 'Missing',
    className: 'bg-amber-50 text-amber-800 border-amber-200 dark:bg-amber-950 dark:text-amber-300 dark:border-amber-900'
  },
  waitingOnTrader: {
    label: 'Needs your action',
    className: 'bg-amber-50 text-amber-800 border-amber-200 dark:bg-amber-950 dark:text-amber-300 dark:border-amber-900'
  },
  awaitingApproval: {
    label: 'Awaiting approval',
    className: 'bg-blue-50 text-blue-800 border-blue-200 dark:bg-blue-950 dark:text-blue-300 dark:border-blue-900'
  },
  approved: {
    label: 'Approved',
    className: 'bg-green-50 text-green-700 border-green-200 dark:bg-green-950 dark:text-green-300 dark:border-green-900'
  }
}

function formatDate(date: KlearDate | null | undefined): string {
  return date ? date.toString('MMM D, YYYY') : '—'
}

function DateCell({ date }: { date: KlearDate | null | undefined }) {
  return <span className="whitespace-nowrap">{formatDate(date)}</span>
}

function distinctIdDisplay(number: string | undefined, idDisplay: string | undefined): string | undefined {
  return idDisplay && idDisplay !== number ? idDisplay : undefined
}

function OrderLink({ row, context }: { row: ReceivableRow; context: ReceivableTableBaseContext }) {
  const { orderId, orderIdDisplay } = row.invoice
  if (!orderIdDisplay) return null
  return (
    <Link
      to={context.orderHref(orderId, row.offer?.id ?? row.invoice.id)}
      className="block text-xs text-muted-foreground underline underline-offset-2 hover:text-foreground"
    >
      {orderIdDisplay}
    </Link>
  )
}

function OfferId({ row }: { row: ReceivableRow }) {
  const idDisplay = row.offer?.idDisplay
  if (!idDisplay) return null
  return <Sub>{idDisplay}</Sub>
}

function NumberWithId({ number, idDisplay }: { number?: string; idDisplay?: string }) {
  const suffix = distinctIdDisplay(number, idDisplay)
  return (
    <div>
      {number || '—'}
      {suffix && <Sub>{suffix}</Sub>}
    </div>
  )
}

function Sub({ children, className }: { children: ReactNode; className?: string }) {
  return <span className={cn('block text-xs text-muted-foreground', className)}>{children}</span>
}

function HeaderWithTip({ title, tip, align }: { title: string; tip?: string; align?: 'right' }) {
  return (
    <div className={cn('flex items-center gap-1', align === 'right' && 'justify-end')}>
      <span>{title}</span>
      {tip && (
        <Tooltip>
          <TooltipTrigger asChild>
            <button type="button" aria-label={tip} className="text-muted-foreground hover:text-foreground">
              <Info className="size-3.5" />
            </button>
          </TooltipTrigger>
          <TooltipContent className="max-w-60">{tip}</TooltipContent>
        </Tooltip>
      )}
    </div>
  )
}

function fileGroups(
  row: ReceivableRow,
  context: ReceivableTableBaseContext,
  documents: readonly (typeof DOCUMENTS)[number][],
  removable = false
): ReceivableFileGroup[] {
  return documents.flatMap((document) => {
    const event:
      | { id: number; idDisplay: string; effectiveDate: KlearDate; number?: string; amount?: Money }
      | undefined = row.documents[document.key].event
    if (event == null) return []
    const previewContext: OrderEventFilePreviewContext = {
      traderId: context.traderId,
      eventId: event.id,
      eventType: document.eventType,
      idDisplay: event.idDisplay,
      order: { idDisplay: row.invoice.orderIdDisplay, displayName: row.invoice.customerName ?? '' },
      ancestors: [],
      details: {
        typeLabel: document.title,
        effectiveDate: event.effectiveDate.toString(),
        number: event.number,
        amountFormatted: event.amount?.toString() ?? null
      }
    }
    return [{ documents: context.documentsFor(event.id), previewContext, removable }]
  })
}

function invoiceColumns(context: ReceivableTableBaseContext): ColumnDef<ReceivableRow>[] {
  return [
    {
      id: 'customer',
      header: 'Customer',
      cell: ({ row }) => (
        <div>
          <span className="font-medium">{row.original.invoice.customerName || '—'}</span>
          <OfferId row={row.original} />
          <OrderLink row={row.original} context={context} />
          {row.original.offer?.isRejected && <Sub>Cancelled</Sub>}
        </div>
      )
    },
    {
      id: 'poNumber',
      header: 'PO #',
      cell: ({ row }) => {
        const { event, isPlaceholder } = row.original.documents.purchaseOrder
        return <NumberWithId number={isPlaceholder ? 'Placeholder' : event?.number} idDisplay={event?.idDisplay} />
      }
    },
    {
      id: 'invoiceNumber',
      header: 'Invoice #',
      cell: ({ row }) => (
        <NumberWithId number={row.original.invoice.number} idDisplay={row.original.invoice.idDisplay} />
      )
    },
    {
      id: 'date',
      header: 'Date',
      cell: ({ row }) => <DateCell date={row.original.invoice.effectiveDate} />
    },
    {
      id: 'terms',
      header: 'Terms',
      cell: ({ row }) =>
        `Net ${KlearDate.daysBetween(row.original.invoice.effectiveDate, row.original.invoice.endDate)}`
    },
    {
      id: 'amount',
      header: () => <div className="text-right">Amount</div>,
      cell: ({ row }) => (
        <div className="text-right tabular-nums">
          {(row.original.offer?.settlementCollection ?? row.original.invoice.amount).toString()}
        </div>
      )
    }
  ]
}

function pricingColumns(tip?: string): ColumnDef<ReceivableRow>[] {
  return [
    {
      id: 'purchasePrice',
      header: () => <HeaderWithTip title="Purchase price" tip={tip} align="right" />,
      cell: ({ row }) => {
        const { offer } = row.original
        if (offer == null) return <div className="text-right">—</div>
        return (
          <div className="text-right tabular-nums">
            {offer.purchasePrice.toString()}
            <Sub>{invoiceOfferAdvanceRateFormatted(offer)} of invoice</Sub>
          </div>
        )
      }
    },
    {
      id: 'fees',
      header: () => <HeaderWithTip title="Fees" tip={tip} align="right" />,
      cell: ({ row }) => <div className="text-right tabular-nums">{row.original.fees?.toString() ?? '—'}</div>
    }
  ]
}

function filesColumn(context: ReceivableTableBaseContext): ColumnDef<ReceivableRow> {
  return {
    id: 'files',
    header: 'Files',
    cell: ({ row }) => (
      <ReceivableFiles traderId={context.traderId} groups={fileGroups(row.original, context, DOCUMENTS)} />
    )
  }
}

export function QuotesTable({ rows, context }: { rows: ReceivableRow[]; context: ReceivableTableContext }) {
  const columns: ColumnDef<ReceivableRow>[] = [
    ...invoiceColumns(context),
    ...pricingColumns(ESTIMATE_TIP),
    filesColumn(context),
    {
      id: 'actions',
      header: '',
      cell: ({ row }) => {
        const { offer } = row.original
        if (offer == null || !context.canAccept) return null
        return (
          <div className="flex justify-end">
            <Button size="sm" onClick={() => context.onAccept(offer)}>
              Accept
            </Button>
          </div>
        )
      }
    }
  ]
  return <DataTable data={rows} columns={columns} loading={context.isLoading} showFilters={false} />
}

export function AwaitingQuoteTable({ rows, context }: { rows: ReceivableRow[]; context: ReceivableTableContext }) {
  const columns: ColumnDef<ReceivableRow>[] = [...invoiceColumns(context), filesColumn(context)]
  return <DataTable data={rows} columns={columns} loading={context.isLoading} showFilters={false} />
}

function documentLine(
  label: string,
  event: { number?: string; idDisplay: string; amount: Money; effectiveDate: KlearDate }
): string {
  const suffix = distinctIdDisplay(event.number, event.idDisplay)
  const name = event.number && suffix ? `${event.number} (${suffix})` : event.idDisplay
  return [`${label} ${name}`, event.amount.toString(), formatDate(event.effectiveDate)].join(' · ')
}

function PendingCustomer({ row, context }: { row: ReceivableRow; context: ReceivableTableBaseContext }) {
  const { event: purchaseOrder, isPlaceholder } = row.documents.purchaseOrder
  return (
    <div>
      <span className="font-medium">{row.invoice.customerName || '—'}</span>
      <OfferId row={row} />
      <OrderLink row={row} context={context} />
      <Sub>{purchaseOrder && !isPlaceholder ? documentLine('PO', purchaseOrder) : 'No purchase order yet'}</Sub>
      <Sub>{documentLine('Invoice', row.invoice)}</Sub>
    </div>
  )
}

export function PendingTable({ rows, context }: { rows: ReceivableRow[]; context: ReceivableTableContext }) {
  const columns: ColumnDef<ReceivableRow>[] = [
    { id: 'customer', header: 'Customer', cell: ({ row }) => <PendingCustomer row={row.original} context={context} /> },
    ...DOCUMENTS.map(
      (document): ColumnDef<ReceivableRow> => ({
        id: document.key,
        header: document.title,
        cell: ({ row }) => {
          const { status, event } = row.original.documents[document.key]
          const isPlaceholder = document.key === 'purchaseOrder' && row.original.documents.purchaseOrder.isPlaceholder
          const hints = event == null ? missingDocumentHints(document.key, row.original) : null
          const badge = DOCUMENT_STATUS_BADGE[status]
          return (
            <div className="flex flex-col items-start gap-1.5">
              <Badge variant="outline" className={cn('text-xs', badge.className)}>
                {badge.label}
              </Badge>
              {isPlaceholder && (
                <Sub>
                  {event != null && context.documentsFor(event.id).length > 0
                    ? 'Files received. Klear will review them.'
                    : 'Placeholder. Add the customer’s purchase order for Klear to file.'}
                </Sub>
              )}
              {event != null && status === 'waitingOnTrader' && (
                <Sub>{`${upperFirst(event.status.toLowerCase())}. Add what is missing for Klear to review again.`}</Sub>
              )}
              {event != null && (
                <ReceivableFiles
                  traderId={context.traderId}
                  groups={fileGroups(row.original, context, [document], status !== 'approved')}
                />
              )}
              {event != null ? (
                <AttachFilesButton orderEventId={event.id} traderId={context.traderId} label={document.title} />
              ) : (
                hints != null && <SendFilesButton traderId={context.traderId} label={document.title} hints={hints} />
              )}
            </div>
          )
        }
      })
    ),
    {
      id: 'actions',
      header: '',
      cell: ({ row }) => {
        const { offer } = row.original
        if (offer == null || !context.canCancel) return null
        return (
          <div className="flex justify-end">
            <Button size="sm" variant="ghost" onClick={() => context.onCancel(offer)}>
              Cancel offer
            </Button>
          </div>
        )
      }
    }
  ]
  return <DataTable data={rows} columns={columns} loading={context.isLoading} showFilters={false} />
}

function RecoveryDue({ endDate }: { endDate: KlearDate | undefined }) {
  if (!endDate) return <>—</>
  const days = KlearDate.daysBetween(KlearDate.today(), endDate)
  return (
    <>
      {formatDate(endDate)}
      {days < 0 ? (
        <Sub className="text-destructive">{`${-days} ${-days === 1 ? 'day' : 'days'} overdue`}</Sub>
      ) : (
        <Sub>{days === 0 ? 'today' : `in ${days} ${days === 1 ? 'day' : 'days'}`}</Sub>
      )}
    </>
  )
}

export function LiveTable({ rows, context }: { rows: ReceivableRow[]; context: ReceivableTableContext }) {
  const columns: ColumnDef<ReceivableRow>[] = [
    ...invoiceColumns(context),
    ...pricingColumns(ESTIMATE_TIP),
    {
      id: 'recoveryDue',
      header: () => <div className="text-right">Advance recovery due</div>,
      cell: ({ row }) => (
        <div className="text-right tabular-nums">
          <RecoveryDue endDate={row.original.offer?.endDate} />
        </div>
      )
    },
    {
      id: 'extensionFees',
      header: () => <div className="text-right">Extension fees</div>,
      cell: ({ row }) => {
        const fee = row.original.unpaidExtensionFees
        return (
          <div className={cn('text-right tabular-nums', fee && 'font-medium text-destructive')}>
            {fee?.toString() ?? '—'}
          </div>
        )
      }
    },
    filesColumn(context),
    {
      id: 'actions',
      header: '',
      cell: ({ row }) => {
        const { offer } = row.original
        if (!offer?.canReturnAdvance) return null
        return (
          <div className="flex justify-end">
            <Button size="sm" variant="outline" onClick={() => context.onPayNow(offer)}>
              Pay now
            </Button>
          </div>
        )
      }
    }
  ]
  return <DataTable data={rows} columns={columns} loading={context.isLoading} showFilters={false} />
}

export function CompletedTable({ rows, context }: { rows: ReceivableRow[]; context: ReceivableTableBaseContext }) {
  const columns: ColumnDef<ReceivableRow>[] = [
    ...invoiceColumns(context),
    ...pricingColumns(),
    {
      id: 'extensionFees',
      header: () => <div className="text-right">Extension fees</div>,
      cell: ({ row }) => {
        const { extensionFeesCharged, unpaidExtensionFees } = row.original
        return (
          <div className="text-right tabular-nums">
            {extensionFeesCharged?.toString() ?? '—'}
            {unpaidExtensionFees && (
              <Sub className="font-medium text-destructive">{`${unpaidExtensionFees.toString()} unpaid`}</Sub>
            )}
          </div>
        )
      }
    },
    { id: 'funded', header: 'Funded', cell: ({ row }) => <DateCell date={row.original.offer?.fundedEffectiveDate} /> },
    {
      id: 'completed',
      header: 'Completed',
      cell: ({ row }) => <DateCell date={row.original.offer?.completedEffectiveDate} />
    },
    filesColumn(context)
  ]
  return <DataTable data={rows} columns={columns} loading={context.isLoading} showFilters={false} />
}
